"use client";

import { useState } from "react";
import { ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Sparkline } from "@/components/kpi-table/sparkline";
import { ScoreSimulatorPanel } from "@/components/score/score-simulator-panel";
import { ScoreTimeMachineChart } from "@/components/score/score-time-machine-chart";
import { formatRatio, isPenceCurrency, descalePencePriceHistory } from "@/lib/format";
import { KPI_METHOD_NOTES } from "@/lib/scoring/scoring";
import type { ScoreResult } from "@/lib/scoring/types";
import type { RetroactiveScorePoint } from "@/lib/scoring/retroactive-score";
import type { ScoreHistoryPoint } from "@/lib/score-history/query";
import { describeScoreTrend } from "@/lib/score-history/trend";
import type { PricePoint } from "@/lib/data/types";
import { cn } from "@/lib/utils";

export function ScoreBreakdownDialog({
  score,
  scoreHistory = [],
  retroactiveScore = [],
  priceHistory = [],
  currency,
  trigger,
  showHint = false,
}: {
  score: ScoreResult;
  scoreHistory?: ScoreHistoryPoint[];
  /** Score-Zeitmaschine (backlog.md -> D3): server-seitig aus computeRetroactiveScoreSeries()
   * vorberechnet, hier nur gerendert - Neuberechnung bei jedem Dialog-Öffnen wäre unnötig. */
  retroactiveScore?: RetroactiveScorePoint[];
  priceHistory?: PricePoint[];
  currency?: string;
  trigger: React.ReactNode;
  /** Zeigt einen zusaetzlichen visuellen "klickbar"-Hinweis (Badge + Hover-Tooltip +
   * leichtes Scale-up) - nur fuer den Score-Ring gedacht (Erik 2026-07-22: "muss
   * ersichtlicher werden, dass es dieses Feature ueberhaupt gibt"). Der zweite,
   * textbasierte Trigger ("Alle 10 Bereiche im Detail ansehen") ist durch Wortlaut +
   * Unterstreichung bereits eindeutig als Link erkennbar und braucht keine Dopplung. */
  showHint?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // Pence-notierte LSE-Aktien (GBp/GBX) liefern priceHistory roh in Pence - nur hier
  // fuer die native Anzeige korrigiert, siehe descalePence()-Kommentar in lib/format.ts.
  const displayPriceHistory = descalePencePriceHistory(priceHistory, currency);
  const displayCurrency = isPenceCurrency(currency) ? "GBP" : currency;

  const triggerButton = (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label="Score-Details anzeigen"
      className={cn(
        "group relative cursor-pointer rounded-full outline-none",
        showHint && "transition-transform duration-200 hover:scale-[1.03] focus-visible:scale-[1.03]",
      )}
    >
      {trigger}
      {showHint && (
        <span className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-ink text-background shadow-md ring-2 ring-card transition-transform group-hover:scale-110">
          <ChevronRight className="h-4 w-4" />
        </span>
      )}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {showHint ? (
        <Tooltip>
          <TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
          <TooltipContent>Details &amp; Verlauf über die Zeit ansehen</TooltipContent>
        </Tooltip>
      ) : (
        triggerButton
      )}
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">
            So setzt sich der Erikonomie-Score zusammen
          </DialogTitle>
          <DialogDescription>
            10 gleich gewichtete Bereiche, je 0–10 Punkte, ausschließlich aus
            unternehmenseigenen Kennzahlen - kein Vergleich gegen einen Sektor-Median mehr.
            Fehlende Bereiche werden aus Rechnung und Anzahl ausgeschlossen, statt mit 0
            bewertet zu werden; der Gesamtscore ist der Durchschnitt der verfügbaren
            Bereiche, hochgerechnet auf 0–100.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {score.kpis.map((kpi) => (
            <div key={kpi.key} className="border-b border-hairline pb-3 last:border-0">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-ink">{kpi.label}</span>
                <span className="shrink-0 font-tabular text-sm text-ink-secondary">
                  {kpi.available ? `${formatRatio(kpi.basePoints, 1)} / 10 Punkte` : "nicht verfügbar"}
                </span>
              </div>
              <p className="mt-0.5 text-xs leading-relaxed text-ink-secondary">{kpi.reason}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{KPI_METHOD_NOTES[kpi.key]}</p>
            </div>
          ))}
        </div>

        <ScoreSimulatorPanel score={score} />

        {retroactiveScore.length >= 2 ? (
          <div className="rounded-lg bg-muted p-3">
            <span className="text-xs font-medium text-ink">
              Score-Zeitmaschine: Kurs &amp; rückwirkend berechneter Score
            </span>
            <div className="mt-2">
              <ScoreTimeMachineChart points={retroactiveScore} priceHistory={displayPriceHistory} currency={displayCurrency} />
            </div>
            <p className="mt-1.5 text-xs leading-relaxed text-ink-secondary">
              Rückwirkend aus den historischen Jahresabschlussdaten neu berechnet (Stand jeweils
              zum Geschäftsjahresende) - eine bewusste Vereinfachung: der Bereich Verschuldung
              (Net Debt/EBITDA) fließt mangels historischer Daten nicht ein. Rein deskriptiv,
              keine rückblickende Bewertung als Signal.
            </p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Für eine rückwirkende Score-Zeitmaschine liegt noch nicht genug historische
            Datentiefe vor.
          </p>
        )}

        {scoreHistory.length >= 2 ? (
          <div className="rounded-lg bg-muted p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-ink">Score-Verlauf</span>
              <Sparkline data={scoreHistory.map((point, i) => ({ year: i, value: point.score }))} />
            </div>
            <p className="mt-1.5 text-xs leading-relaxed text-ink-secondary">
              {describeScoreTrend(scoreHistory)}
            </p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Der Score-Verlauf wird ab jetzt täglich aufgezeichnet und erscheint hier in den
            kommenden Tagen.
          </p>
        )}

        <p className="text-xs text-muted-foreground">
          Datenqualität: {score.dataQuality}/10 Bereiche mit verfügbaren Daten.
        </p>
      </DialogContent>
    </Dialog>
  );
}
